/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RouteComponentProps } from "react-router-dom";
import Switch from "react-switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faFingerprint,
} from "@fortawesome/free-solid-svg-icons";

import ApiService from "../../_services/ApiService";
import AuthenticationService from "../../_services/AuthenticationService";
import { AlertDialog } from "../../_components/alert-dialog/AlertDialog";
import { ReactComponent as CameraIcon } from "../../assets/icons/camera-solid.svg";

// Reuse the original attendance stylesheet for layout/skeleton primitives,
// then layer the refined visual on top via attendance-node.css.
import "../attendance/attendance.css";
import "./attendance-node.css";

// ============================================================================
// Types
// ============================================================================

interface MatchParams {
  batchIds: string;
}

interface Topic {
  id: number;
  name: string;
}

interface Batch {
  id: number;
  name: string;
  topics?: Topic[];
}

interface ActivitySlotTpl {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  topicId?: number;
  topicName: string;
  instructorId?: number;
  instructorName?: string;
}

interface TimetableTpl {
  id: number;
  name: string;
  activitySlotTemplates?: ActivitySlotTpl[];
}

interface StudentApi {
  id: number;
  enrollmentId: number;
  name: string;
  studentCode: string;
  batchId: number;
  hasAttendance?: boolean;
  attendance?: number; // 0 = Present, 1 = Absent (matches Java convention)
}

interface StudentRow {
  student: StudentApi;
  attendanceStatus: boolean; // true = Present
  bioMetricAttendnace: boolean | null;
}

interface FormState {
  slotDate: string;
  timeTableId: number;
  activitySlotTemplateId: number;
  startTime: string;
  endTime: string;
  minutesLate: string;
  topicId: number;
  topicName: string;
  instructorId: number;
}

// ============================================================================
// Helpers
// ============================================================================

const todayDDMMYYYY = (): string => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const parseIdList = (raw?: string): number[] =>
  (raw || "")
    .split(",")
    .map((s) => parseInt(s, 10))
    .filter((n) => Number.isInteger(n) && n > 0)
    .filter((id, i, arr) => arr.indexOf(id) === i);

const readInitConfig = (): any => {
  try {
    return JSON.parse(localStorage.getItem("initData") || "{}");
  } catch {
    return {};
  }
};

// ============================================================================
// Component
// ============================================================================

const AttendanceNode: React.FC<RouteComponentProps<MatchParams>> = (props) => {
  const config = useMemo(readInitConfig, []);
  const currentUser = AuthenticationService.currentUserValue;

  // ---- URL params ------------------------------------------------------
  const urlBatchIds = useMemo(
    () => parseIdList(props.match.params.batchIds),
    [props.match.params.batchIds]
  );
  const query = useMemo(
    () => new URLSearchParams(props.location.search),
    [props.location.search]
  );
  const targetTemplateSlotId =
    parseInt(query.get("templateSlotId") || "0", 10) || null;
  const urlInstructorId =
    parseInt(query.get("instructorId") || "0", 10) || null;
  const urlInstructorName = query.get("instructorName");

  const effectiveInstructorId =
    urlInstructorId || currentUser?.instructorId || -1;

  // ---- State -----------------------------------------------------------
  const [selectedBatches, setSelectedBatches] = useState<Batch[]>([]);
  const [timetables, setTimetables] = useState<TimetableTpl[]>([]);
  const [activitySlotTemplates, setActivitySlotTemplates] = useState<
    ActivitySlotTpl[]
  >([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [instructors, setInstructors] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedInstructorId, setSelectedInstructorId] = useState<number>(
    effectiveInstructorId
  );
  const [form, setForm] = useState<FormState>({
    slotDate: todayDDMMYYYY(),
    timeTableId: -100,
    activitySlotTemplateId: -1,
    startTime: config.startTimeForAttendance || "",
    endTime: config.endTimeForAttendance || "",
    minutesLate: "",
    topicId: -1,
    topicName: "",
    instructorId: effectiveInstructorId,
  });
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [sortBy, setSortBy] = useState<"default" | "name" | "code">("default");
  const biometricMap = useRef<Map<number, boolean>>(new Map());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attendanceAlreadyTaken, setAttendanceAlreadyTaken] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [successDialog, setSuccessDialog] = useState<{
    title: string;
    message: string;
  } | null>(null);

  // ---- Slot selection logic -------------------------------------------
  const applySelectedSlot = useCallback(
    (
      timetableList: TimetableTpl[],
      instructorId: number,
      targetSlotId: number | null
    ): void => {
      let tt: TimetableTpl | null = null;
      let slot: ActivitySlotTpl | null = null;

      // Prefer explicit slot from URL
      if (targetSlotId) {
        for (const t of timetableList) {
          const s = t.activitySlotTemplates?.find((x) => x.id === targetSlotId);
          if (s) {
            tt = t;
            slot = s;
            break;
          }
        }
      }

      // Fall back to today + instructor match
      if (!slot) {
        const today = todayDDMMYYYY();
        for (const t of timetableList) {
          const s = t.activitySlotTemplates?.find(
            (x) => x.date === today && x.instructorId === instructorId
          );
          if (s) {
            tt = t;
            slot = s;
            break;
          }
        }
      }

      if (tt && slot) {
        setActivitySlotTemplates(tt.activitySlotTemplates || []);
        setForm((f) => ({
          ...f,
          slotDate: slot!.date,
          timeTableId: tt!.id,
          activitySlotTemplateId: slot!.id,
          startTime: slot!.startTime,
          endTime: slot!.endTime,
          topicId: slot!.topicId || -1,
          topicName: slot!.topicName,
        }));
        if (slot.topicId) {
          setTopics([{ id: slot.topicId, name: slot.topicName }]);
        }
      } else {
        setActivitySlotTemplates([]);
        setForm((f) => ({
          ...f,
          timeTableId: -100,
          activitySlotTemplateId: -1,
        }));
      }
    },
    []
  );

  // ---- Initial fetch: batches → timetables → slot selection -----------
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        // 1. Fetch all batches accessible to this instructor and narrow to the URL ones
        const batchRes: any = await ApiService.syncBatches(
          effectiveInstructorId
        );
        if (cancelled) return;
        const allBatches: Batch[] = batchRes.data?.data || [];
        const matched = allBatches.filter((b) => urlBatchIds.includes(b.id));

        // Warn if any URL batches are not mapped to this instructor.
        // Resolve the unknown IDs to names via getBatchesByIds (Node) so
        // the message is human-readable without leaking names into the URL.
        const missing = urlBatchIds.filter(
          (id) => !matched.find((b) => b.id === id)
        );
        if (missing.length > 0) {
          let labels = missing.map((id) => `Batch ID: ${id}`);
          try {
            const namesRes: any = await ApiService.getBatchesByIds(missing);
            const found: any[] = namesRes.data?.data || namesRes.data || [];
            const byId = new Map<number, string>(
              found.map((b: any) => [b.id, b.name || b.batchCode || `Batch ${b.id}`])
            );
            labels = missing.map((id) =>
              byId.has(id) ? `${byId.get(id)} (ID: ${id})` : `Batch ID: ${id}`
            );
          } catch {
            /* fall back to bare IDs */
          }
          if (cancelled) return;
          setAlert({
            title: "Batch-Instructor Mapping Missing",
            message:
              `The following batches are assigned to this timetable slot but ` +
              `are not mapped to instructor "${urlInstructorName || "this instructor"}":\n\n` +
              `${labels.join(", ")}\n\nPlease update the Instructor-Batch mapping.`,
          });
        }

        setSelectedBatches(matched);

        if (matched.length === 0) return;

        // 2. Fetch timetables for each batch
        const ttResponses: any[] = await Promise.all(
          matched.map((b) =>
            ApiService.syncTimeTables(effectiveInstructorId, [b.id])
          )
        );
        if (cancelled) return;
        const allTimetables: TimetableTpl[] = ttResponses.flatMap(
          (r) => r.data?.data || []
        );
        setTimetables(allTimetables);

        // 3. Derive instructor list from slots
        const map = new Map<number, string>();
        allTimetables.forEach((t) =>
          t.activitySlotTemplates?.forEach((s) => {
            if (s.instructorId && !map.has(s.instructorId)) {
              map.set(
                s.instructorId,
                s.instructorName || `Instructor ${s.instructorId}`
              );
            }
          })
        );
        if (urlInstructorId && urlInstructorName && !map.has(urlInstructorId)) {
          map.set(urlInstructorId, urlInstructorName);
        }
        setInstructors(
          Array.from(map.entries()).map(([id, name]) => ({ id, name }))
        );

        // 4. Pre-select slot (URL → fallback to today+instructor)
        applySelectedSlot(
          allTimetables,
          effectiveInstructorId,
          targetTemplateSlotId
        );

        // Seed topics from the first batch as a default for non-standard timetable
        const topicsFromBatch = matched[0]?.topics || [];
        if (topicsFromBatch.length && !targetTemplateSlotId) {
          setTopics(topicsFromBatch);
          setForm((f) => ({
            ...f,
            topicId: topicsFromBatch[0].id,
            topicName: topicsFromBatch[0].name,
          }));
        }

        // 5. Fetch biometric attendance for each batch (cached in ref)
        const bioRes: any[] = await Promise.all(
          matched.map((b) =>
            ApiService.getBioMetricStudentAttendanceNode(b.id)
          )
        );
        if (cancelled) return;
        bioRes.forEach((r) => {
          (r.data?.data || []).forEach((item: any) => {
            biometricMap.current.set(item.enrollmentId, item.attendanceStatus);
          });
        });
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to initialize attendance screen:", err);
        }
      }
    };

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Handlers --------------------------------------------------------
  const handleInstructorChange = (instructorId: number) => {
    setSelectedInstructorId(instructorId);
    setForm((f) => ({ ...f, instructorId }));
    applySelectedSlot(timetables, instructorId, null);
  };

  const handleTimetableChange = (timetableId: number) => {
    if (timetableId === -100) {
      setActivitySlotTemplates([]);
      setForm((f) => ({
        ...f,
        timeTableId: -100,
        activitySlotTemplateId: -1,
        startTime: config.startTimeForAttendance || "",
        endTime: config.endTimeForAttendance || "",
      }));
      // Show batch-level topics for non-standard
      const t = selectedBatches[0]?.topics || [];
      setTopics(t);
      if (t.length > 0) {
        setForm((f) => ({ ...f, topicId: t[0].id, topicName: t[0].name }));
      }
      return;
    }
    const tt = timetables.find((t) => t.id === timetableId);
    const slots = tt?.activitySlotTemplates || [];
    setActivitySlotTemplates(slots);
    if (slots.length) {
      const slot = slots[0];
      setForm((f) => ({
        ...f,
        timeTableId: timetableId,
        activitySlotTemplateId: slot.id,
        startTime: slot.startTime,
        endTime: slot.endTime,
        topicId: slot.topicId || -1,
        topicName: slot.topicName,
      }));
      if (slot.topicId) {
        setTopics([{ id: slot.topicId, name: slot.topicName }]);
      }
    } else {
      setForm((f) => ({
        ...f,
        timeTableId: timetableId,
        activitySlotTemplateId: -1,
      }));
    }
  };

  const handleClassChange = (activitySlotTemplateId: number) => {
    if (activitySlotTemplateId === -1) {
      setForm((f) => ({ ...f, activitySlotTemplateId: -1 }));
      return;
    }
    const slot = activitySlotTemplates.find(
      (s) => s.id === activitySlotTemplateId
    );
    if (slot) {
      setForm((f) => ({
        ...f,
        activitySlotTemplateId,
        startTime: slot.startTime,
        endTime: slot.endTime,
        topicId: slot.topicId || -1,
        topicName: slot.topicName,
      }));
      if (slot.topicId) {
        setTopics([{ id: slot.topicId, name: slot.topicName }]);
      }
    }
  };

  const handleTopicChange = (topicId: number) => {
    const t = topics.find((x) => x.id === topicId);
    if (t) {
      setForm((f) => ({ ...f, topicId: t.id, topicName: t.name }));
    }
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (selectedBatches.length === 0) {
      e.batchId = `*Select ${config.batchDisplayName || "Batch"}`;
    }
    if (
      form.timeTableId !== -100 &&
      form.activitySlotTemplateId === -1
    ) {
      e.activitySlotTemplateId = `*Select ${config.classDisplayName || "Class"}`;
    }
    if (!form.startTime) e.startTime = "*Select Start Time";
    if (!form.endTime) e.endTime = "*Select End Time";
    if (form.timeTableId <= 0 && form.startTime > form.endTime) {
      e.endTime = "*End Time must be greater than Start Time";
    }
    if (form.topicId === -1 || form.topicId === 0) {
      e.topicId = `*Select ${config.topicDisplayName || "Topic"}`;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleGetStudents = async () => {
    if (!validate()) return;
    setLoading(true);
    setStudents([]);
    setAttendanceAlreadyTaken(false);
    try {
      const responses: any[] = await Promise.all(
        selectedBatches.map((b) =>
          ApiService.syncStudentsForBatchNode(
            b.id,
            targetTemplateSlotId || undefined
          )
        )
      );
      const flatRaw: StudentApi[] = responses.flatMap((r) => r.data?.data || []);
      // Dedupe by enrollment id (student.id): the same enrollment can be
      // returned by more than one selected batch, and the save endpoint rejects
      // any enrollment that appears more than once per save.
      const seenIds = new Set<number>();
      const flat: StudentApi[] = flatRaw.filter((s) => {
        if (seenIds.has(s.id)) return false;
        seenIds.add(s.id);
        return true;
      });
      const anyTaken = flat.some((s) => s.hasAttendance !== undefined);
      const rows: StudentRow[] = flat.map((s) => ({
        student: s,
        // attendance === 0 means Present (Java convention preserved in Node)
        attendanceStatus:
          s.hasAttendance !== undefined ? s.attendance === 0 : true,
        bioMetricAttendnace: biometricMap.current.has(s.id)
          ? biometricMap.current.get(s.id)!
          : null,
      }));
      setStudents(rows);
      setAttendanceAlreadyTaken(anyTaken);
    } catch (err) {
      console.error("Failed to load students:", err);
      setAlert({
        title: "ERROR",
        message: "Failed to load students. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStudent = (studentId: number) => {
    setStudents((rows) =>
      rows.map((r) =>
        r.student.id === studentId
          ? { ...r, attendanceStatus: !r.attendanceStatus }
          : r
      )
    );
  };

  // Sorted view (does NOT mutate `students` — toggles use student.id, not index)
  const sortedStudents = useMemo(() => {
    if (sortBy === "default") return students;
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });
    const key: (r: StudentRow) => string =
      sortBy === "name"
        ? (r) => r.student.name || ""
        : (r) => r.student.studentCode || "";
    return [...students].sort((a, b) => collator.compare(key(a), key(b)));
  }, [students, sortBy]);

  const handleMarkAll = (status: boolean) => {
    setStudents((rows) => rows.map((r) => ({ ...r, attendanceStatus: status })));
  };

  const handleSave = async () => {
    if (form.activitySlotTemplateId <= 0) {
      setAlert({
        title: "ERROR",
        message:
          "Cannot save: please pick a class (activity slot) from the timetable before saving attendance.",
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        batchIds: selectedBatches.map((b) => b.id),
        studentAttendanceDetails: students.map((r) => ({
          student: r.student,
          attendanceStatus: r.attendanceStatus,
          bioMetricAttendnace: r.bioMetricAttendnace,
        })),
        startTime: form.startTime || undefined,
        endTime: form.endTime || undefined,
        topicId: form.topicId > 0 ? form.topicId : undefined,
        instructorId: form.instructorId > 0 ? form.instructorId : undefined,
        slotStatus: "Conducted" as const,
      };
      await ApiService.saveActivitySlotAttendance(
        form.activitySlotTemplateId,
        payload
      );

      const present = students.filter((r) => r.attendanceStatus).length;
      const absent = students.length - present;
      setSuccessDialog({
        title: "ATTENDANCE SAVE SUCCESS",
        message:
          `<b>Attendance saved successfully.</b>` +
          `<br/>Number of Students Present : <b style='color:green'>${present}</b>` +
          `<br/>Number of Students Absent : <b style='color:red'>${absent}</b>`,
      });
    } catch (err: any) {
      setAlert({
        title: "ATTENDANCE SAVE ERROR",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to save attendance.",
      });
    } finally {
      setSaving(false);
    }
  };

  // Navigate to the Node-backed Lessonplan screen, passing the current
  // batch/topic context the way the Java attendance flow opens /lessonplan.
  const goToLessonPlan = () => {
    const batch = selectedBatches[0];
    if (!batch || !(form.topicId > 0)) {
      setAlert({
        title: "Lesson Plan",
        message: `Please select a ${config.batchDisplayName || "batch"} and ${
          config.topicDisplayName || "topic"
        } first.`,
      });
      return;
    }
    props.history.push({
      pathname: "/lesson-plan-node",
      state: {
        batchId: batch.id,
        batchName: batch.name,
        topicId: form.topicId,
        topicName: form.topicName,
        activitySlotId: form.activitySlotTemplateId,
        startTime: form.startTime,
        endTime: form.endTime,
      },
    });
  };

  // ---- Derived values -------------------------------------------------
  const batchHeading =
    selectedBatches.length > 0
      ? selectedBatches.map((b) => b.name).join(", ")
      : `--Select ${config.batchDisplayName || "Batch"}--`;

  // ---- Render ---------------------------------------------------------
  return (
    <div className="attendance attendance-v2">
      {alert && (
        <AlertDialog
          message={alert.message}
          title={alert.title}
          btnOkText="OK"
          size="lg"
          close={() => setAlert(null)}
        />
      )}
      {successDialog && (
        <AlertDialog
          message={successDialog.message}
          title={successDialog.title}
          btnOkText="OK"
          size="lg"
          close={() => {
            setSuccessDialog(null);
            // Mirror the Java attendance flow (displayLessonPlan): on OK, open the
            // lesson plan if the tenant shows it after attendance, else go back.
            if (config.showLessonplanAfterAttendance) {
              goToLessonPlan();
            } else {
              props.history.goBack();
            }
          }}
        />
      )}

      <div className="app flex-row align-items-center">
        <div className="container">
          <div className="card-group">
            <div className="card text-center border-info full-height1">
              {/* Header */}
              <div className="card-header pageheading bg-info text-white">
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  size="lg"
                  className="float-left"
                  onClick={() => props.history.goBack()}
                />
                <b>Attendance</b>
                <CameraIcon className="float-right attendance-camera-icon" />
              </div>

              <div className="card-body">
                {/* Batch heading */}
                <div className="row">
                  <div className="col text-left">
                    <h5>{batchHeading}</h5>
                  </div>
                  {errors.batchId && (
                    <div className="text-danger">{errors.batchId}</div>
                  )}
                </div>

                {/* Instructor */}
                {instructors.length > 0 && (
                  <div className="row">
                    <div className="col col-5 text-left">
                      <label className="my-label">Instructor</label>
                    </div>
                    <div className="col text-right">
                      <select
                        className="form-control"
                        value={selectedInstructorId}
                        onChange={(e) =>
                          handleInstructorChange(parseInt(e.target.value, 10))
                        }
                      >
                        {instructors.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Timetable */}
                <div className="row">
                  <div className="col col-5 text-left">
                    <label className="my-label">Timetable</label>
                  </div>
                  <div className="col text-right">
                    <select
                      className="form-control"
                      value={form.timeTableId}
                      onChange={(e) =>
                        handleTimetableChange(parseInt(e.target.value, 10))
                      }
                    >
                      {timetables.map((tt) => (
                        <option key={tt.id} value={tt.id}>
                          {tt.name}
                        </option>
                      ))}
                      <option value={-100}>Non Standard</option>
                    </select>
                  </div>
                </div>

                {/* Class */}
                <div className="row">
                  <div className="col col-5 text-left">
                    <label className="my-label">
                      {config.classDisplayName || "Class"}
                    </label>
                  </div>
                  <div className="col text-right">
                    <select
                      className="form-control"
                      value={form.activitySlotTemplateId}
                      onChange={(e) =>
                        handleClassChange(parseInt(e.target.value, 10))
                      }
                    >
                      {activitySlotTemplates.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.topicName}
                        </option>
                      ))}
                      {(activitySlotTemplates.length === 0 ||
                        form.activitySlotTemplateId === -1) && (
                        <option value={-1}>#None</option>
                      )}
                    </select>
                  </div>
                  {errors.activitySlotTemplateId && (
                    <div className="text-danger">
                      {errors.activitySlotTemplateId}
                    </div>
                  )}
                </div>

                {/* Start Time */}
                <div className="row">
                  <div className="col col-5 text-left">
                    <label className="my-label">Start Time</label>
                  </div>
                  <div className="col text-right my-label">{form.startTime}</div>
                  {errors.startTime && (
                    <div className="text-danger">{errors.startTime}</div>
                  )}
                </div>

                {/* End Time */}
                <div className="row">
                  <div className="col col-5 text-left">
                    <label className="my-label">End Time</label>
                  </div>
                  <div className="col text-right my-label">{form.endTime}</div>
                  {errors.endTime && (
                    <div className="text-danger">{errors.endTime}</div>
                  )}
                </div>

                {/* Minutes Late */}
                <div className="row">
                  <div className="col col-5 text-left">
                    <label className="my-label">Minutes Late</label>
                  </div>
                  <div className="col text-right">
                    <input
                      className="form-control"
                      type="number"
                      placeholder="Minutes Late"
                      value={form.minutesLate}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, minutesLate: e.target.value }))
                      }
                    />
                  </div>
                </div>

                {/* Topic */}
                <div className="row">
                  <div className="col col-5 text-left">
                    <label className="my-label">
                      {config.topicDisplayName || "Topic"}
                    </label>
                  </div>
                  <div className="col text-right">
                    <select
                      className="form-control"
                      value={form.topicId}
                      onChange={(e) =>
                        handleTopicChange(parseInt(e.target.value, 10))
                      }
                    >
                      {topics.length > 0 ? (
                        topics.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))
                      ) : (
                        <option value={-1}>#None</option>
                      )}
                    </select>
                  </div>
                  {errors.topicId && (
                    <div className="text-danger">{errors.topicId}</div>
                  )}
                </div>

                {/* Get Students */}
                <div className="row">
                  <button
                    type="button"
                    className="btn btn-primary btn-lg btn-block"
                    onClick={handleGetStudents}
                    disabled={loading}
                  >
                    {loading ? "Loading Students..." : "Get Students"}
                  </button>
                </div>

                {/* Loading skeleton */}
                {loading && (
                  <div className="skeleton-loader-container">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="skeleton-student-row">
                        <div className="skeleton-switch" />
                        <div className="skeleton-student-info">
                          <div className="skeleton-line skeleton-code" />
                          <div className="skeleton-line skeleton-name" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Attendance already taken notice */}
                {attendanceAlreadyTaken && students.length > 0 && (
                  <div
                    style={{
                      backgroundColor: "var(--status-warning-bg)",
                      border: "1px solid var(--status-warning)",
                      borderLeft: "4px solid var(--status-warning)",
                      borderRadius: "4px",
                      padding: "12px 16px",
                      marginTop: "16px",
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <span style={{ fontSize: "20px" }}>ℹ️</span>
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "var(--status-warning-text)",
                          marginBottom: "4px",
                        }}
                      >
                        Attendance Already Taken
                      </div>
                      <div style={{ fontSize: "14px", color: "var(--status-warning-text)" }}>
                        Attendance has already been recorded for this slot. You
                        can modify and save to update the existing attendance.
                      </div>
                    </div>
                  </div>
                )}

                {/* Student count summary */}
                {!loading && students.length > 0 && (
                  <div className="att-count-summary">
                    <div className="att-count-total">
                      Students <span>({students.length})</span>
                    </div>
                    <div className="att-sort-group">
                      <label htmlFor="att-sort" className="att-sort-label">
                        Sort by
                      </label>
                      <select
                        id="att-sort"
                        className="att-sort-select"
                        value={sortBy}
                        onChange={(e) =>
                          setSortBy(
                            e.target.value as "default" | "name" | "code"
                          )
                        }
                      >
                        <option value="default">Default</option>
                        <option value="name">Name (A–Z)</option>
                        <option value="code">Student Code (A–Z)</option>
                      </select>
                    </div>
                    <div className="att-count-breakdown">
                      <span className="att-count-present">
                        {students.filter((r) => r.attendanceStatus).length} Present
                      </span>
                      <span className="att-count-absent">
                        {students.filter((r) => !r.attendanceStatus).length} Absent
                      </span>
                    </div>
                  </div>
                )}

                {/* Mark All buttons */}
                {!loading && students.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      padding: "10px 0",
                      marginBottom: "10px",
                    }}
                  >
                    <button
                      type="button"
                      style={{
                        flex: 1,
                        backgroundColor: "var(--status-success)",
                        color: "var(--text-on-brand)",
                        border: "none",
                        borderRadius: "4px",
                        padding: "10px 16px",
                        fontSize: "16px",
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                      onClick={() => handleMarkAll(true)}
                    >
                      Mark All Present
                    </button>
                    <button
                      type="button"
                      style={{
                        flex: 1,
                        backgroundColor: "var(--status-danger)",
                        color: "var(--text-on-brand)",
                        border: "none",
                        borderRadius: "4px",
                        padding: "10px 16px",
                        fontSize: "16px",
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                      onClick={() => handleMarkAll(false)}
                    >
                      Mark All Absent
                    </button>
                  </div>
                )}

                <br />

                {/* Student rows */}
                {sortedStudents.map((row, i) => {
                  const bio = row.bioMetricAttendnace;
                  const fingerprintClass =
                    bio === null || bio === undefined
                      ? "text-muted"
                      : bio === true
                      ? "text-secondary"
                      : "text-danger";
                  return (
                    <div key={row.student.id}>
                      <div className="row">
                        <div className="col col-auto align-self-center att-sno">
                          {i + 1}
                        </div>
                        <div className="col col-auto align-self-center align-items-center">
                          <label>
                            <Switch
                              checkedIcon
                              uncheckedIcon
                              className="react-switch"
                              checked={row.attendanceStatus}
                              onChange={() => handleToggleStudent(row.student.id)}
                            />
                          </label>
                        </div>
                        <div className="col text-left align-self-center align-items-center">
                          <span>
                            {row.student.studentCode}
                            <br />
                            {row.student.name}
                          </span>
                          <FontAwesomeIcon
                            icon={faFingerprint}
                            size="lg"
                            className={`float-right ${fingerprintClass}`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Save Attendance */}
                {students.length > 0 && (
                  <div className="row attendance-save-row">
                    <button
                      type="button"
                      className={`btn btn-lg btn-block ${
                        saving ? "btn-secondary" : "btn-primary"
                      }`}
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? (
                        <span>
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          />{" "}
                          Saving Attendance...
                        </span>
                      ) : (
                        "SAVE ATTENDANCE"
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceNode;
